/**
 * One word for "is the shell where it should be on this phone".
 *
 * The raw numbers in the debug overlay don't answer that on their own: what
 * counts as a good value for any one of them depends on which way round the
 * viewport bug goes on the device in hand, which is exactly what took three
 * attempts to pin down on the one phone we could measure. Checking a device
 * nobody here owns shouldn't need that context — it should be three taps and
 * a word.
 *
 * Two equalities say it, and neither needs to know where the viewport sits on
 * the screen or which viewport unit to believe:
 *
 * 1. The shell is as tall as the screen. The tab bar is the last thing in the
 *    shell, so the shell's bottom edge is the bar's bottom edge; if the shell
 *    is the screen, the bar is on the bottom of the screen.
 * 2. The document is not scrolled. It has slack to be scrolled by wherever
 *    the engine under-reports the viewport, and using that slack takes the
 *    bar off the bottom — the "sometimes" this all started as.
 * 3. The bar keeps the whole safe-area inset clear. Sitting on the bottom of
 *    the screen is only right if the bar's *content* stops above the band
 *    reserved for the home indicator — the swipe-up gesture is live in it,
 *    so a tap target reaching in is a tap the system can take instead.
 *
 * Only meaningful installed: in a browser tab the shell is deliberately the
 * *small* viewport, short of the screen by whatever Safari's toolbar occupies.
 */
export type LayoutVerdict = { ok: boolean; text: string };

export const layoutVerdict = ({
  standalone,
  shellHeight,
  screenHeight,
  scrollY,
  barPaddingBottom,
  safeBottom,
}: {
  standalone: boolean;
  /** The shell's own rendered height, or 0 when it isn't mounted. */
  shellHeight: number;
  /** window.screen.height — the hardware, in CSS pixels. */
  screenHeight: number;
  /** The document's scroll offset, which should never leave 0 inside the shell. */
  scrollY: number;
  /** The tab bar's bottom padding — what holds its content clear of the band. */
  barPaddingBottom: number;
  /** env(safe-area-inset-bottom), the band iOS reserves for the home indicator. */
  safeBottom: number;
}): LayoutVerdict => {
  if (!standalone) return { ok: true, text: "kun i app" };
  if (!shellHeight) return { ok: true, text: "ingen skal" };
  // A point of rounding either way is the device's pixel grid, not a bug.
  const short = Math.round(screenHeight - shellHeight);
  if (Math.abs(short) > 1) {
    return {
      ok: false,
      text: short > 0 ? `skal ${short}px for kort` : `skal ${-short}px for høj`,
    };
  }
  if (Math.round(scrollY) !== 0) return { ok: false, text: `dokument rullet ${Math.round(scrollY)}px` };
  const intrusion = Math.round(safeBottom - barPaddingBottom);
  if (intrusion > 1) return { ok: false, text: `bjælke ${intrusion}px inde i sikker zone` };
  return { ok: true, text: "ok" };
};

export default layoutVerdict;
